import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { useMediaQuery, Box, Container, Toolbar } from '@mui/material';

// project import
import Drawer from './Drawer';
import Header from './Header';
// import Footer from './Footer';
import HorizontalBar from './Drawer/HorizontalBar';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import ReportSecondaryMenu from 'sections/sbs/ReportSecondaryMenu';
import LpAnalysisSecondaryMenuFlat from 'sections/lp-analysis/LpAnalysisSecondaryMenuFlat';
import ChatSidebar from './ChatSidebar';

import navigation from 'menu-items';
import useConfig from 'hooks/useConfig';
import { openDrawer } from 'store/reducers/menu';
import { openModal } from 'store/reducers/actions';
import { LAYOUT_CONST } from 'config';
import { getCompetitorsSearchData } from 'utils/competitorsDataUtils';
// import Banner from 'sections/banners/banner';

// Field categories for SBS report - moved from SideBySideReport.js
const FIELD_CATEGORIES = {
  'Side-by-Side Analysis': {
    sectionId: 'section-sbs-analysis',
    fields: [
      { name: 'Overview', id: 'section-sbs-overview' },
      { name: 'Competitive Index', id: 'section-competitive-index' },
      { name: 'Feature Matrix', id: 'section-feature-matrix' },
      { name: 'Heatmap Scorecard', id: 'section-heatmap-scorecard' },
      { name: 'Positioning Matrices', id: 'section-positioning-matrices' }
    ]
  },
  'USP & Distinctions': {
    dataPath: 'analysis_data.usp',
    sectionId: 'section-usp',
    fields: ['USP', 'DISTINCTION'].map((field) => ({
      name: field,
      id: `usp-${field.toLowerCase().replace(/\s+/g, '-')}`
    }))
  },
  Overview: {
    dataPath: 'analysis_data.overview_result.result.0',
    sectionId: 'section-overview-fields',
    fields: [
      'Main Value Proposition',
      'USP',
      'Competitive advantages',
      'Strengths',
      'Differentiation',
      'CTA',
      'Pricing',
      'Audience',
      'Industry',
      'Use Cases',
      'Description',
      'Positioning',
      'Key Features',
      'Social Proof',
      'Tone of Voice',
      'Trust Factors',
      'Brand Positioning'
    ].map((field) => ({
      name: field,
      id: `field-${field.toLowerCase().replace(/\s+/g, '_')}`
    }))
  },
  'Market Trends': {
    dataPath: 'analysis_data.market_trends',
    sectionId: 'section-marketTrends',
    fields: ['trends', 'benchmarks'].map((field) => ({
      name: field,
      id: `market-${field.toLowerCase().replace(/\s+/g, '-')}`
    }))
  },
  'Pricing Analysis': {
    dataPath: 'analysis_data.pricing_analysis',
    sectionId: 'section-pricing',
    fields: ['pricing_model', 'pricing_strategy', 'pricing_conclusion'].map((field) => ({
      name: field,
      id: `pricing-${field.toLowerCase().replace(/\s+/g, '-')}`
    }))
  }
};

// Field categories for LP Analysis report
const LP_ANALYSIS_CATEGORIES = {
  'Overview': {
    sectionId: 'overview',
    singleSection: true,
    fields: [
      'Service Name',
      'Main Value Proposition',
      'Description',
      'Key Features',
      'Target Audience',
      'Brand Positioning',
      'Tone of Voice',
      'Trust Factors',
      'Social Proof',
      'CTA',
      'Pricing'
    ].map((field) => ({
      name: field,
      id: `overview-${field.toLowerCase().replace(/\s+/g, '_')}`
    }))
  },
  'USP Analysis': {
    sectionId: 'usp',
    fields: [
      'USP',
      'Distinction'
    ].map((field) => ({
      name: field,
      id: `usp-${field.toLowerCase().replace(/\s+/g, '_')}`
    }))
  },
  
  'Market Overview': {
    sectionId: 'market-overview',
    fields: [
      'Introduction',
      'Challenges',
      'Key Trends',
      'Opportunities',
      'Defining Features',
      'Market Size and Growth'
    ].map((field) => ({
      name: field,
      id: `market-${field.toLowerCase().replace(/\s+/g, '_')}`
    }))
  },
  'Market Trends': {
    sectionId: 'market-trends',
    fields: [
      'Trends',
      'Benchmarks'
    ].map((field) => ({
      name: field,
      id: `market-trends-${field.toLowerCase().replace(/\s+/g, '_')}`
    }))
  },
  'SWOT Analysis': {
    sectionId: 'swot',
    fields: [
      'Strengths',
      'Weaknesses',
      'Opportunities',
      'Threats'
    ].map((field) => ({
      name: field,
      id: `swot-${field.toLowerCase().replace(/\s+/g, '_')}`
    }))
  },
  'Audience Segmentation': {
    sectionId: 'segmentation',
    singleSection: true
  },
  'ICP Profile': {
    sectionId: 'icp-profile',
    fields: [
      'Demographics',
      'Psychographics',
      'Behavioral Patterns',
      'Pain Points',
      'Goals',
      'Decision Factors'
    ].map((field) => ({
      name: field,
      id: `icp-${field.toLowerCase().replace(/\s+/g, '_')}`
    }))
  }
};

// Aliases for LP Analysis menu categories (menu title -> config key)
const LP_ANALYSIS_CATEGORY_ALIASES = {
  'Ideal Customer Profile': 'ICP Profile',
};

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownLG = useMediaQuery(theme.breakpoints.down('xl'));
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const { container, miniDrawer, menuOrientation } = useConfig();
  const dispatch = useDispatch();

  const menu = useSelector((state) => state.menu);
  const { drawerOpen, secondaryMenuVisible, lpAnalysisMenuVisible } = menu;
  const extendedAnalysisResults = useSelector((state) => state.extendedAnalysisReducer.extendedAnalysisResults);
  const historyResult = useSelector((state) => state.historyReducer.historyResult);

  const [isChatOpen, setIsChatOpen] = useState(false);

  // Secondary menu state
  const [activeField, setActiveField] = useState(null);
  const [menuMinimized, setMenuMinimized] = useState(false);

  // Minimize menu by default on screens smaller than md
  useEffect(() => {
    if (downMD) {
      setMenuMinimized(true);
    }
  }, [downMD]);

  // Determine which type of secondary menu to show based on current route
  const isLpAnalysisPage = location.pathname.includes('/lp-analysis');
  const isSbsPage = location.pathname.includes('/project-dashboard') || location.pathname.includes('/competitors');
  


  // Handle field selection in secondary menu
  const handleFieldSelect = ({ field, category }) => {
    const fieldName = field.name || field;
    setActiveField(fieldName);

    // Handle LP Analysis fields
    if (isLpAnalysisPage) {
      console.log('LP Analysis field select:', { field, category, fieldName });

      // Handle Competitors section
      if (category === 'Competitors') {
        // If a specific competitor is selected from the dropdown
        if (field && field.id) {
          const competitorCard = document.getElementById(field.id);
          if (competitorCard) {
            const elementPosition = competitorCard.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - 80; // More offset for sticky header
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
          return;
        }
        // If the main category is clicked
        const competitorsSection = document.getElementById('competitors');
        if (competitorsSection) {
          const elementPosition = competitorsSection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - 50;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
        return;
      }

      // Map menu title to config key if alias exists
      const canonicalCategory = LP_ANALYSIS_CATEGORY_ALIASES[category] || category;
      const handleLpAnalysisFieldSelect = () => {
        // For Overview section, scroll to the overview section
        if (canonicalCategory === 'Overview') {
          const overviewSection = document.getElementById('overview');
          if (overviewSection) {
            const elementPosition = overviewSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - 50;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
          return;
        }

        // Special case: Audience Segmentation
        if (canonicalCategory === 'Audience Segmentation') {
          const segSection = document.getElementById('segmentation');
          if (segSection) {
            const elementPosition = segSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - 50;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
          return;
        }

        // Special case: ICP Profile
        if (canonicalCategory === 'ICP Profile') {
          let icpSection = document.getElementById('icp-profile');
          if (!icpSection) {
            icpSection = document.querySelector('[id*="icp"]');
          }
          if (icpSection) {
            const elementPosition = icpSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - 50;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
          return;
        }

        // Special case: Pricing Analysis
        if (canonicalCategory === 'Pricing Analysis') {
          const pricingSection = document.getElementById('pricing-analysis');
          if (pricingSection) {
            const elementPosition = pricingSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - 50;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
          return;
        }

        // Special case: Reviews Analysis
        if (canonicalCategory === 'Reviews Analysis') {
          const reviewsSection = document.getElementById('reviews-analysis');
          if (reviewsSection) {
            const elementPosition = reviewsSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - 50;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
          return;
        }

        // For other sections, try to find the section by ID
        const sectionId = LP_ANALYSIS_CATEGORIES[canonicalCategory]?.sectionId;
        console.log('Looking for section:', { category, canonicalCategory, sectionId });
        if (sectionId) {
          // Check if this is a specific subsection (like SWOT subsections or USP subsections)
          if (fieldName && fieldName !== canonicalCategory) {
            // Try to find the specific subsection
            const subsectionId = `${sectionId}-${fieldName.toLowerCase().replace(/\s+/g, '_')}`;
            let element = document.getElementById(subsectionId);
            
            // If not found, try alternative ID formats
            if (!element) {
              element = document.getElementById(`swot-${fieldName.toLowerCase()}`);
            }
            if (!element) {
              element = document.getElementById(`usp-${fieldName.toLowerCase()}`);
            }
            if (!element) {
              element = document.getElementById(`market-${fieldName.toLowerCase().replace(/\s+/g, '_')}`);
            }
            if (!element) {
              element = document.getElementById(`icp-${fieldName.toLowerCase().replace(/\s+/g, '_')}`);
            }
            if (!element) {
              element = document.getElementById(`segmentation-${fieldName.toLowerCase().replace(/\s+/g, '_')}`);
            }
            if (!element) {
              element = document.getElementById(`market-trends-${fieldName.toLowerCase().replace(/\s+/g, '_')}`);
            }
            
            if (element) {
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.scrollY - 50;
              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
              return;
            }
          }
          
          // Fallback to main section
          let element = document.getElementById(sectionId);
          
          // Special handling for ICP Profile - try alternative IDs
          if (!element && sectionId === 'icp-profile') {
            element = document.getElementById('icp-profile');
          }
          if (!element && sectionId === 'icp-profile') {
            element = document.querySelector('[id*="icp"]');
          }
          
          if (element) {
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - 50;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }
      };

      handleLpAnalysisFieldSelect();
      return;
    }

    // Handle SBS fields (existing logic)
    if (isSbsPage) {
      // Check if this is a locked SBS feature
      if (category === 'Side-by-Side Analysis' && !analysisAccess?.features?.side_by_side_analysis?.enabled) {
        // Trigger paywall modal for locked SBS features
        dispatch(
          openModal({
            title: 'Upgrade to Pro',
            subtitle: 'Get access to Side-by-Side Analysis and unlock all premium features.',
            trial: true
          })
        );
        return;
      }

      // Special handling for Overview fields which need special attention
      if (category === 'Overview') {
        // Click the All Fields button immediately without waiting
        const allFieldsButton =
          document.querySelector('[data-field-index="all"]') || document.querySelector('.MuiGrid-item > div:first-of-type');

        if (allFieldsButton) {
          allFieldsButton.click();
        }

        // Try to find pricing button specifically for pricing field
        if (fieldName === 'Pricing') {
          // Find all elements that could be pricing buttons or tabs
          const pricingButtons = Array.from(document.querySelectorAll('button, [role="tab"]')).filter(
            (el) => el.textContent && el.textContent.trim().toLowerCase() === 'pricing'
          );

          // Click the first pricing button if found
          if (pricingButtons.length > 0) {
            pricingButtons[0].click();
          }
        }

        // Try multiple ID formats immediately
        const tryScrollToField = () => {
          const formattedName = fieldName.toLowerCase().replace(/\s+/g, '_');

          // Try multiple possible ID formats and element selectors
          const selectors = [
            `#field-${formattedName}`, // Standard ID format
            `#${formattedName}`, // Simple ID
            `[data-field="${fieldName}"]`, // Data attribute
            `.field-${formattedName}`, // Class based
            `h5:contains("${fieldName}")`, // Heading with text
            `h6:contains("${fieldName}")`, // Subheading with text
            `.MuiTypography-root:contains("${fieldName}")`, // Any Typography with text
            `div:contains("${fieldName}")` // Any div with text
          ];

          // Find the first matching element
          let targetElement = null;
          for (const selector of selectors) {
            try {
              // JQuery-like contains selector doesn't work directly, so handle it specially
              if (selector.includes(':contains(')) {
                const tag = selector.split(':contains(')[0];
                const searchText = selector.split(':contains("')[1].split('")')[0];

                // Get all elements of this tag type
                const elements = document.querySelectorAll(tag);

                // Find the first one containing our text
                for (const el of elements) {
                  if (el.textContent && el.textContent.includes(searchText)) {
                    targetElement = el;
                    break;
                  }
                }

                if (targetElement) break;
              } else {
                // Normal selector
                const found = document.querySelector(selector);
                if (found) {
                  targetElement = found;
                  break;
                }
              }
            } catch (e) {
              // Ignore selector errors and try the next one
              console.error(`Error with selector ${selector}:`, e);
            }
          }

          // If we found an element, scroll to it
          if (targetElement) {
            // Get closest parent with an ID if possible
            const scrollTarget = targetElement.closest('[id]') || targetElement;

            // Calculate position with minimum offset
            const elementPosition = scrollTarget.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - 50;

            // Scroll immediately
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
            return true;
          }

          return false;
        };

        // Try to scroll immediately
        const scrolled = tryScrollToField();

        // If direct scrolling failed, try the overview section
        if (!scrolled) {
          const overviewSection = document.getElementById('section-overview-fields');
          if (overviewSection) {
            const elementPosition = overviewSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - 50;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }

        return;
      }

      // Handle other categories with their normal pattern
      let fieldId;
      if (category === 'USP & Distinctions') {
        fieldId = 'section-usp';
      } else if (category === 'Market Trends') {
        fieldId = `market-${fieldName.toLowerCase().replace(/\s+/g, '-')}`;
      } else if (category === 'Side-by-Side Analysis') {
        fieldId = field.id;
      } else {
        fieldId = field.id || `field-${fieldName.toLowerCase().replace(/\s+/g, '_')}`;
      }

      // Scroll handling for non-Overview fields - do it immediately
      const element = document.getElementById(fieldId);
      if (!element) return;

      // Calculate position with minimum offset
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - 50;

      // Scroll immediately
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Check if extended analysis is complete (when polling stops)
  const isExtendedAnalysisComplete = !extendedAnalysisResults?.processing_status;
  
  // Reviews analysis menu item should appear when extended analysis is complete
  const shouldShowReviewsAnalysis = isExtendedAnalysisComplete;



  // Dynamically generate LP analysis categories based on available data
  const getLpAnalysisCategories = () => {
    const competitorsTask = getCompetitorsSearchData(historyResult, extendedAnalysisResults);
    let competitorsCategory = null;

    if (competitorsTask) {
      const isProcessing = competitorsTask.status?.task !== 'success';
      const finalResult = competitorsTask.results?.final_answer?.result;
      const competitorsList = [...(finalResult?.results || []), ...(finalResult?.emerging || [])];

      if (isProcessing && !finalResult) {
        // Still processing, no results yet
        competitorsCategory = {
          sectionId: 'competitors',
          loading: true
        };
      } else if (competitorsList.length > 0) {
        // Finished and has results
        competitorsCategory = {
          sectionId: 'competitors',
          fields: competitorsList.map((c) => ({
            name: c.title,
            // Sanitize URL to create a valid ID
            id: `competitor-card-${c.url.replace(/[^a-zA-Z0-9]/g, '-')}`
          }))
        };
      }
    }

    // Check if pricing analysis is available
    const hasPricingAnalysis = extendedAnalysisResults?.pricing_analysis?.has_pricing_info;

    // Create a new categories object with the desired order
    const categories = {};
    
    // Add Overview first
    categories['Overview'] = LP_ANALYSIS_CATEGORIES['Overview'];
    
    // Add USP Analysis second
    categories['USP Analysis'] = LP_ANALYSIS_CATEGORIES['USP Analysis'];
    
    // Add Competitors third if it exists
    if (competitorsCategory) {
      categories['Competitors'] = competitorsCategory;
    }
    
    // Add all other categories in their original order
    Object.keys(LP_ANALYSIS_CATEGORIES).forEach(key => {
      if (key !== 'Overview' && key !== 'USP Analysis') {
        categories[key] = LP_ANALYSIS_CATEGORIES[key];
      }
    });

    // Add Pricing Analysis after SWOT if available
    if (hasPricingAnalysis) {
      categories['Pricing Analysis'] = {
        sectionId: 'pricing-analysis',
        singleSection: true
      };
    }

    // Add Reviews Analysis as the last item when extended analysis is complete
    if (shouldShowReviewsAnalysis) {
      categories['Reviews Analysis'] = {
        sectionId: 'reviews-analysis',
        singleSection: true
      };
    }

    return categories;
  };

  const handleChatToggle = (isOpen) => {
    setIsChatOpen(isOpen);
  };

  const handleMenuMinimizeToggle = (minimized) => {
    setMenuMinimized(minimized);
  };

  const isHorizontal = menuOrientation === LAYOUT_CONST.HORIZONTAL_LAYOUT && !downLG;

  // drawer toggler
  const [open, setOpen] = useState(!miniDrawer || drawerOpen);
  const handleDrawerToggle = () => {
    setOpen(!open);
    dispatch(openDrawer({ drawerOpen: !open }));
  };

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      setOpen(!matchDownLG);
      dispatch(openDrawer({ drawerOpen: !matchDownLG }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDownLG]);

  useEffect(() => {
    if (open !== drawerOpen) setOpen(drawerOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  // Auto-close sidebar on specific routes
  useEffect(() => {
    // Add substrings to match any path that includes them
    const autoCollapsePaths = ['/project-dashboard'];

    if (autoCollapsePaths.some((path) => location.pathname.includes(path))) {
      setOpen(false);
      dispatch(openDrawer({ drawerOpen: false }));
    }
  }, [location.pathname, dispatch]);

  // Calculate the appropriate margin for the main content
  const getContentMargin = () => {
    if (secondaryMenuVisible || lpAnalysisMenuVisible) {
      return menuMinimized ? '0px' : '180px';
    }
    return '0px';
  };
  const analysisAccess = useSelector(
    (state) => state.competitorsReducer.competitorsAnalysisResults?.access
  );
  // const userPlan = useSelector((state) => state.proAccountReducer.plan);
  // const isPaidUser = userPlan !== 'Free';
  return (
    <>
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Header open={open} handleDrawerToggle={handleDrawerToggle} />

        {!isHorizontal ? <Drawer open={open} handleDrawerToggle={handleDrawerToggle} /> : <HorizontalBar />}

        {/* Secondary Report Menu - Only visible when secondaryMenuVisible is true */}
        {secondaryMenuVisible && isSbsPage && (
          <ReportSecondaryMenu
            categories={FIELD_CATEGORIES}
            activeField={activeField}
            onFieldSelect={handleFieldSelect}
            visible={true}
            onMinimizeToggle={handleMenuMinimizeToggle}
            access={analysisAccess}
            minimized={menuMinimized}
          />
        )}

        {/* LP Analysis Secondary Menu - Only visible when lpAnalysisMenuVisible is true and on LP analysis page */}
        {lpAnalysisMenuVisible && isLpAnalysisPage && (
          <LpAnalysisSecondaryMenuFlat
            categories={getLpAnalysisCategories()}
            activeField={activeField}
            onFieldSelect={handleFieldSelect}
            onMinimizeToggle={handleMenuMinimizeToggle}
            minimized={menuMinimized}
          />
        )}

        <Box
          component="main"
          sx={{
            width: `calc(100% - ${drawerOpen && !downLG ? '260px' : '0px'})`,
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            ml: downLG ? 0 : `${getContentMargin()}`,
            mr: isChatOpen ? '400px' : 0,
            transition: 'margin-left 0.3s ease-in-out, margin-right 0.3s ease-in-out'
          }}
        >
          <Toolbar sx={{ mt: downLG ? '0px' : menuOrientation === LAYOUT_CONST.HORIZONTAL_LAYOUT ? 2.5 : 0 }} />
          <Container
            maxWidth={container ? 'xl' : false}
            sx={{
              ...(container && { px: { xs: 0, sm: 2 } }),
              position: 'relative',
              minHeight: 'calc(100vh - 110px)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Breadcrumbs navigation={navigation} title titleBottom card={false} divider={false} />
            <Outlet />
            {/* <Footer /> */}
          </Container>
        </Box>
      </Box>
      <ChatSidebar onToggle={handleChatToggle} />
    </>
  );
};

export default MainLayout;
